import { ServiceUnreachableError } from '@fleece/utilities';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Menu, Moon, SlidersHorizontal, Sun } from 'lucide-react';
import { useState } from 'react';
import { ConnectionControl } from '@/components/connection-control';
import { Sidebar } from '@/components/nav';
import { PreferencesDialog } from '@/components/preferences-dialog';
import { SetupScreen } from '@/components/setup-screen';
import { Button, Spinner } from '@/components/ui';
import { ConnectionProvider, useConnection } from '@/hooks/use-connection';
import { PreferencesProvider } from '@/hooks/use-preferences';
import { useAccountId, useRoute } from '@/hooks/use-route';
import { useTheme } from '@/hooks/use-theme';
import { easternDate } from '@/lib/format';
import type { Route } from '@/lib/route';
import { AccountPage } from '@/pages/account-page';
import { AccountsPage } from '@/pages/accounts-page';
import { CreditSpreadsPage } from '@/pages/credit-spreads-page';
import { CredentialsPage } from '@/pages/credentials-page';
import { NotifiersPage } from '@/pages/notifiers-page';

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        // Retry only what a retry can fix: the service being momentarily unreachable. A bad
        // ticker or a refused backtest answers the same way the second time.
        retry: (failures, error) => failures < 2 && error instanceof ServiceUnreachableError,
      },
      mutations: { retry: false },
    },
  });
}

/** Deliberately almost nothing: one round trip, usually to the same machine. */
function ConnectingSplash() {
  return (
    <div role="status" className="flex items-center justify-center gap-2 py-24 text-xs text-muted">
      <Spinner />
      Connecting…
    </div>
  );
}

/**
 * A gate rather than a branch inside each panel: everything on the page needs a client to
 * call, so rendering it without one means every panel discovering the same missing answer
 * separately.
 */
function ConnectedPage({ route }: { route: Route }) {
  const { state } = useConnection();
  const accountId = useAccountId();
  if (state.status === 'probing') {
    return <ConnectingSplash />;
  }
  if (state.status === 'setup') {
    return <SetupScreen initial={state.candidate} outcome={state.outcome} />;
  }
  // Keyed by service, so switching to another one starts the page afresh: a backtest id,
  // a symbol or a picked spread from the last service means nothing to the next, and
  // polling it with the old backtest's id is the request the switch is meant to prevent.
  switch (route) {
    case 'accounts':
      // Keyed by account too, so moving from one to another does not carry over the symbol
      // its transactions were narrowed to.
      return accountId === undefined ? <AccountsPage key={state.connection.apiUrl} /> : <AccountPage key={`${state.connection.apiUrl} ${accountId}`} accountId={accountId} />;
    case 'credentials':
      return <CredentialsPage key={state.connection.apiUrl} />;
    case 'notifiers':
      return <NotifiersPage key={state.connection.apiUrl} />;
    case 'credit-spreads':
      return <CreditSpreadsPage key={state.connection.apiUrl} />;
  }
}

function Shell() {
  const [theme, toggleTheme] = useTheme();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [today] = useState(() => easternDate(Date.now()));
  const route = useRoute();
  return (
    <>
      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-30 h-12 shrink-0 border-b border-line bg-page/90 backdrop-blur">
          <div className="flex h-full items-center gap-2 px-2 sm:px-3">
            <Button variant="ghost" className="lg:hidden" onClick={() => setNavOpen(true)} aria-label="Open navigation" aria-expanded={navOpen}>
              <Menu className="size-4" />
            </Button>
            <a href="#/" className="text-sm font-bold tracking-tight lg:w-52 lg:pl-1">
              Fleece
            </a>
            <span className="ml-auto" />
            <ConnectionControl />
            <Button variant="ghost" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
              {theme === 'dark' ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
            </Button>
            <Button variant="ghost" onClick={() => setPreferencesOpen(true)} aria-label="Display preferences" aria-haspopup="dialog">
              <SlidersHorizontal className="size-3.5" />
            </Button>
          </div>
        </header>
        <div className="flex flex-1">
          <Sidebar current={route} open={navOpen} onClose={() => setNavOpen(false)} />
          <main className="min-w-0 flex-1">
            <ConnectedPage route={route} />
          </main>
        </div>
      </div>
      <PreferencesDialog open={preferencesOpen} today={today} onClose={() => setPreferencesOpen(false)} />
    </>
  );
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      {/* Inside the query client, because switching service clears its cache. */}
      <ConnectionProvider>
        <PreferencesProvider>
          <Shell />
        </PreferencesProvider>
      </ConnectionProvider>
    </QueryClientProvider>
  );
}
